/**
 * In-app sign-up screen (FR-2.11) — username + email + password + date of birth. Email is
 * required by the UI (password recovery needs a deliverable address — D-43.2), and a
 * neutral date of birth is required for the 13+ age gate (FR-14.1, #41): the field is a
 * plain date picker (not a yes/no), and the client pre-checks age before the round trip
 * while the server stays authoritative. Client-validates the password against the pool
 * policy; the hook then posts to the server-mediated register endpoint (admin-create;
 * Cognito self-signup is disabled by Epoxy) and signs the user straight in via SRP — there
 * is no emailed-code step.
 */

import { useState } from 'react';
import type { DateOfBirth } from '@jeopardy/shared';
import {
  parseDateOfBirthInput,
  validateDateOfBirth,
  validateEmail,
  validatePassword,
  validateUsername,
} from './validation';

export interface SignUpFormProps {
  busy: boolean;
  error: string | null;
  notice: string | null;
  onSubmit: (
    username: string,
    email: string,
    password: string,
    dateOfBirth: DateOfBirth,
  ) => void;
  onSignIn: () => void;
}

export function SignUpForm({
  busy,
  error,
  notice,
  onSubmit,
  onSignIn,
}: SignUpFormProps): React.JSX.Element {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [dob, setDob] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    const invalid =
      validateUsername(username) ??
      validateEmail(email) ??
      validatePassword(password) ??
      validateDateOfBirth(dob);
    if (invalid) {
      setLocalError(invalid);
      return;
    }
    const dateOfBirth = parseDateOfBirthInput(dob);
    if (!dateOfBirth) {
      // Unreachable once validateDateOfBirth passes, but keeps the type non-null.
      setLocalError('Enter a valid date of birth.');
      return;
    }
    setLocalError(null);
    onSubmit(username.trim(), email.trim(), password, dateOfBirth);
  };

  const shownError = localError ?? error;

  return (
    <form className="auth-form" data-testid="auth-signup" onSubmit={submit} noValidate>
      <h2 className="auth-form__title">Create an account</h2>
      {notice && (
        <div className="auth-form__notice" data-testid="auth-notice">
          {notice}
        </div>
      )}
      <input
        aria-label="Username"
        data-testid="auth-username"
        placeholder="Username"
        autoComplete="username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        autoFocus
      />
      <input
        type="email"
        aria-label="Email"
        data-testid="auth-email"
        placeholder="Email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <input
        type="password"
        aria-label="Password"
        data-testid="auth-password"
        placeholder="Password (8+ chars, a number, a lowercase letter)"
        autoComplete="new-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <label className="auth-form__field-label" htmlFor="auth-dob">
        Date of birth
      </label>
      <input
        id="auth-dob"
        type="date"
        aria-label="Date of birth"
        data-testid="auth-dob"
        autoComplete="bday"
        value={dob}
        onChange={(e) => setDob(e.target.value)}
      />
      <button
        type="submit"
        className="btn btn--primary"
        data-testid="auth-submit"
        disabled={busy}
      >
        {busy ? 'Creating…' : 'Create account'}
      </button>
      {shownError && (
        <div className="error-text" data-testid="auth-error">
          {shownError}
        </div>
      )}
      <div className="auth-form__links">
        <button
          type="button"
          className="btn btn--link"
          data-testid="auth-goto-signin"
          onClick={onSignIn}
        >
          Already have an account? Sign in
        </button>
      </div>
    </form>
  );
}
